import { useState, useEffect } from "react";
import { Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Sidebar } from "./Sidebar";
import { NotificationsPopover } from "../notifications/NotificationsPopover";
import PurchaseNotifications from "../notifications/PurchaseNotifications";
import { SubscriptionWarning } from "../subscription/SubscriptionGuard";
import TrialBanner from "../trial/TrialBanner";
import TrialWelcomeModal from "../trial/TrialWelcomeModal";
import TrialReminderModal from "../trial/TrialReminderModal";
import TrialExtensionNotice from "../trial/TrialExtensionNotice";
import CurrencyQuickEdit from "../CurrencyQuickEdit";
import ShiftSwitcher from "../ShiftSwitcher";
import SyncStatusIndicator from "../SyncStatusIndicator";
import VersionBadge from "../VersionBadge";
import PendingSyncWarning from "../PendingSyncWarning";
import { Menu, Calculator, LogOut } from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";

const API = `${window.location.origin}/api`;

export const DashboardLayout = () => {
  const { isAuthenticated, loading, user, logout } = useAuth();
  const language = "ar";
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState("");

  // ✨ وضع البساطة للكاشير: على شاشة POS، اخفي القائمة والـ header
  const isCashier = !!user?.is_employee && user?.role === "cashier";
  const isPOSRoute = location.pathname === "/pos" || location.pathname.startsWith("/pos/");
  const kioskMode = isCashier && isPOSRoute;

  useEffect(() => {
    if (isAuthenticated) {
      fetchSettings();
    }
  }, [isAuthenticated]);

  // ✨ Cashier: لو وصل إلى /dashboard، حوّله مباشرة إلى /pos (immediate redirect)
  useEffect(() => {
    if (isCashier && (location.pathname === "/dashboard" || location.pathname === "/")) {
      navigate("/pos", { replace: true });
    }
  }, [isCashier, location.pathname, navigate]);

  // ✨ Immediate redirect (qualifies for SSR / first render)
  if (isCashier && (location.pathname === "/dashboard" || location.pathname === "/")) {
    return <Navigate to="/pos" replace />;
  }

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setWhatsappNumber(response.data.whatsapp_number || "");
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center animate-pulse">
            <Calculator className="w-12 h-12 text-white" />
          </div>
          <p className="mt-4 text-slate-400">{language === "ar" ? "جاري التحميل..." : "Loading..."}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // ✨ Kiosk Mode للكاشير على شاشة POS: ملء الشاشة بدون قائمة جانبية أو header
  if (kioskMode) {
    return (
      <div className="min-h-screen bg-slate-900 noise-overlay" dir="rtl" data-testid="cashier-kiosk-mode">
        <SubscriptionWarning />
        <PendingSyncWarning />

        {/* شريط علوي بسيط: اسم الكاشير + زر تسجيل خروج فقط */}
        <header className="sticky top-0 z-30 bg-gradient-to-l from-slate-900 to-slate-800 border-b border-emerald-700/40 px-3 py-2.5 shadow-lg">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Calculator className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-sm leading-tight" data-testid="kiosk-cashier-name">
                  {user?.name || "كاشير"}
                </p>
                <p className="text-emerald-400 text-[10px] font-semibold uppercase tracking-wider">
                  وضع الكاشير
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <SyncStatusIndicator compact />
              <Button
                onClick={() => { logout(); navigate("/login", { replace: true }); }}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="kiosk-logout-btn"
              >
                <LogOut className="w-4 h-4 me-1.5" />
                إنهاء الوردية
              </Button>
            </div>
          </div>
        </header>

        <main className="min-h-[calc(100vh-60px)] p-2 sm:p-4">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-slate-900 noise-overlay"
      dir={language === "ar" ? "rtl" : "ltr"}
    >
      {/* Trial Welcome Modal - يظهر مرة واحدة عند أول دخول */}
      <TrialWelcomeModal />
      <TrialReminderModal />
      <TrialExtensionNotice />
      
      {/* Subscription Warning Overlay (when expired) */}
      <SubscriptionWarning />
      
      {/* Trial Banner - شريط علوي للتجربة المجانية */}
      <TrialBanner />
      
      {/* Pending Sync Warning - تحذير العمليات المعلقة */}
      <PendingSyncWarning />
      
      <div className="flex">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {/* Main Content */}
        <main className="flex-1 min-w-0 min-h-screen overflow-x-hidden">
          {/* Mobile Header */}
          <header className="lg:hidden sticky top-0 z-30 bg-slate-900/95 backdrop-blur border-b border-slate-700 px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(true)}
                className="text-slate-300 shrink-0"
              >
                <Menu size={24} />
              </Button>
              <div className="flex items-center gap-2 shrink-0">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading font-bold text-white text-sm">
                  Nazario
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <VersionBadge compact />
                <SyncStatusIndicator compact />
                <ShiftSwitcher />
                <CurrencyQuickEdit compact />
                <NotificationsPopover whatsappNumber={whatsappNumber} />
                <PurchaseNotifications />
              </div>
            </div>
          </header>

          {/* Desktop Notifications - Fixed position */}
          <div className="hidden lg:flex fixed top-4 left-4 z-50 gap-2 items-center">
            <VersionBadge />
            <SyncStatusIndicator />
            <ShiftSwitcher />
            <CurrencyQuickEdit />
            <PurchaseNotifications />
            <NotificationsPopover whatsappNumber={whatsappNumber} />
          </div>

          {/* Page Content */}
          <div className="p-4 md:p-6 lg:p-8 overflow-x-hidden">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
